import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useGmailApi } from '../../hooks/useGmailApi';
import { gmailApi } from '../../services/api';
import {
  Mail,
  Send,
  Clock,
  Play,
  Save,
  Info,
  ArrowLeft,
  Paperclip,
  X,
  CheckCircle,
  Loader2,
  Zap,
} from 'lucide-react';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.background};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[6]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing[4]};
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]};
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  color: ${({ theme }) => theme.colors.textPrimary};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.primary}15;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1.5rem;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[6]};
  padding: ${({ theme }) => theme.spacing[6]};
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing[4]};
    gap: ${({ theme }) => theme.spacing[4]};
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  padding: ${({ theme }) => theme.spacing[5]};
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const TriggerContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[4]};
  overflow-x: auto;
  padding-bottom: ${({ theme }) => theme.spacing[2]};
  width: 100%;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.background};
    border-radius: ${({ theme }) => theme.borders.radius.md};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borders.radius.md};

    &:hover {
      background: ${({ theme }) => theme.colors.primary}50;
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    gap: ${({ theme }) => theme.spacing[3]};
  }
`;

interface TriggerCardProps {
  selected: boolean;
  gradient: string;
}

const TriggerCard = styled.button<TriggerCardProps>`
  min-width: 220px;
  height: 160px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[3]};
  border: 2px solid ${({ selected, theme }) => selected ? theme.colors.primary : theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  background: ${({ selected, gradient, theme }) =>
    selected ? gradient : theme.colors.backgroundSecondary};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: ${({ gradient }) => gradient};
    opacity: ${({ selected }) => selected ? 1 : 0};
    transition: opacity 0.3s;
  }

  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.lg};
    border-color: #ff3b30;

    &::before {
      opacity: 1;
      background: #ff3b30;
    }

    svg, span {
      color: #fff;
    }

    p {
      color: rgba(255,255,255,0.9);
    }
  }

  svg {
    position: relative;
    z-index: 1;
    width: 32px;
    height: 32px;
    color: ${({ selected, theme }) => selected ? '#fff' : theme.colors.textPrimary};
    transition: color 0.3s;
  }

  span {
    position: relative;
    z-index: 1;
    font-size: 0.95rem;
    font-weight: 600;
    color: ${({ selected, theme }) => selected ? '#fff' : theme.colors.textPrimary};
    transition: color 0.3s;
  }

  p {
    position: relative;
    z-index: 1;
    font-size: 0.8rem;
    color: ${({ selected, theme }) => selected ? 'rgba(255,255,255,0.9)' : theme.colors.textSecondary};
    text-align: center;
    margin: 0;
    padding: 0 ${({ theme }) => theme.spacing[3]};
    transition: color 0.3s;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    min-width: 180px;
    height: 140px;

    svg {
      width: 28px;
      height: 28px;
    }

    span {
      font-size: 0.9rem;
    }

    p {
      font-size: 0.75rem;
    }
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const Label = styled.label`
  font-size: 0.95rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textPrimary};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const Input = styled.input`
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.95rem;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}15;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const Textarea = styled.textarea`
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.95rem;
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}15;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const InfoBox = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.primary}10;
  border: 1px solid ${({ theme }) => theme.colors.primary}30;
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textSecondary};

  svg {
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.primary};
    margin-top: 2px;
  }
`;

const AttachmentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const FileInput = styled.input`
  display: none;
`;

const UploadButton = styled.label`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.background};
  border: 1px dashed ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  align-self: flex-start;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primary}05;
  }

  svg {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const FileList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const FileChip = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.full};
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.textPrimary};

  button {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2px;
    background: transparent;
    border: none;
    cursor: pointer;
    color: ${({ theme }) => theme.colors.textSecondary};
    transition: color 0.2s;

    &:hover {
      color: ${({ theme }) => theme.colors.error};
    }

    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

const PreviewBox = styled.div`
  padding: ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.primary}30;
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: 1.6;
`;

const PreviewSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const PreviewItem = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  align-items: flex-start;

  svg {
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.primary};
    margin-top: 2px;
  }
`;

const PreviewContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  flex: 1;
`;

const PreviewTitle = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const PreviewDetail = styled.div`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  padding-left: ${({ theme }) => theme.spacing[4]};
`;

const PreviewEmpty = styled.div`
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  padding: ${({ theme }) => theme.spacing[4]};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  justify-content: flex-end;
  margin-top: ${({ theme }) => theme.spacing[4]};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;

    button {
      width: 100%;
    }
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[5]};
  background: ${({ variant, theme }) =>
    variant === 'secondary' ? 'transparent' : theme.colors.primary};
  border: 1px solid ${({ variant, theme }) =>
    variant === 'secondary' ? theme.colors.border : theme.colors.primary};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  color: ${({ variant, theme }) =>
    variant === 'secondary' ? theme.colors.textPrimary : '#fff'};
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ variant, theme }) =>
    variant === 'secondary' ? theme.colors.backgroundSecondary : theme.colors.primaryDark};
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;

    &:hover {
      transform: none;
    }
  }

  .spinning {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

interface Action {
  type: 'sendEmail';
  to: string;
  subject: string;
  body: string;
}

const GmailAutomationSimple: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useCurrentUser();
  const userId = user?.uid;
  const { hasToken, isLoadingTokenConfig } = useGmailApi(userId);
  const isEditMode = !!id;

  const [name, setName] = useState('');
  const [trigger, setTrigger] = useState<'manual' | 'emailReceived' | 'scheduled'>('manual');
  const [labelFilter, setLabelFilter] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [actions, setActions] = useState<Action[]>([{
    type: 'sendEmail',
    to: '',
    subject: '',
    body: ''
  }]);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [savedAttachments, setSavedAttachments] = useState<{ name: string; content: string; mimeType: string }[]>([]);
  const [explanationText, setExplanationText] = useState('Deixe em branco para todos os emails');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const hasShownToast = useRef(false);

  // Redirecionar se não tiver Gmail conectado
  useEffect(() => {
    // Reset do flag quando token for detectado
    if (hasToken) {
      hasShownToast.current = false;
      return;
    }

    // Só redirecionar se userId estiver presente e loading terminou
    if (userId && !isLoadingTokenConfig && !hasToken && !hasShownToast.current) {
      hasShownToast.current = true;
      toast.error('Você precisa conectar sua conta Gmail primeiro');
      navigate('/gmail');
    }
  }, [hasToken, isLoadingTokenConfig, navigate, userId]);

  const triggerOptions = [
    {
      type: 'manual' as const,
      icon: Play,
      label: 'Manual',
      description: 'Execute quando quiser',
      gradient: '#ff3b30'
    },
    {
      type: 'emailReceived' as const,
      icon: Mail,
      label: 'Ao receber email',
      description: 'Automático ao chegar email',
      gradient: '#ff3b30'
    },
    {
      type: 'scheduled' as const,
      icon: Clock,
      label: 'Agendado',
      description: 'Execute em horário específico',
      gradient: '#ff3b30'
    }
  ];

  // Dynamic explanation text with 1s debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (trigger === 'emailReceived') {
        if (labelFilter.trim()) {
          setExplanationText(`Executará apenas para emails com a label: "${labelFilter}"`);
        } else {
          setExplanationText('Executará para todos os emails recebidos');
        }
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [labelFilter, trigger]);

  // Load automation data in edit mode
  useEffect(() => {
    const loadAutomation = async () => {
      if (!isEditMode || !id || !userId) return;

      setIsLoading(true);
      try {
        const response = await gmailApi.getAutomation(id, userId);

        if (response.status === 'success' && response.data) {
          const auto = response.data;
          setName(auto.name || '');
          setTrigger(auto.trigger?.type || 'manual');
          setLabelFilter(auto.trigger?.labelFilter || '');
          setScheduleTime(auto.trigger?.scheduleTime || '');
          setActions(auto.actions || [{
            type: 'sendEmail',
            to: '',
            subject: '',
            body: ''
          }]);

          // Carregar anexos salvos se existirem
          if (auto.actions && auto.actions.length > 0 && auto.actions[0].attachments) {
            setSavedAttachments(auto.actions[0].attachments);
          }
        } else {
          toast.error('Automação não encontrada');
          navigate('/gmail/automations');
        }
      } catch (error) {
        console.error('[GmailAutomationSimple] Erro ao carregar automação:', error);
        toast.error('Erro ao carregar automação');
        navigate('/gmail/automations');
      } finally {
        setIsLoading(false);
      }
    };

    loadAutomation();
  }, [id, isEditMode, userId, navigate]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);

      // Validate file size (max 10MB per file)
      const validFiles = newFiles.filter(file => {
        if (file.size > 10 * 1024 * 1024) {
          alert(`Arquivo ${file.name} é muito grande (máx 10MB)`);
          return false;
        }
        return true;
      });

      setAttachments([...attachments, ...validFiles]);
    }
  };

  const removeFile = (fileToRemove: File) => {
    setAttachments(attachments.filter(file => file !== fileToRemove));
  };

  const removeSavedAttachment = (attachmentName: string) => {
    setSavedAttachments(savedAttachments.filter(att => att.name !== attachmentName));
  };

  const handleSave = async () => {
    if (!userId) {
      toast.error('Você precisa estar logado para salvar automações');
      return;
    }

    // Validate name
    if (!name || name.trim() === '') {
      toast.error('Por favor, dê um nome para a automação');
      return;
    }

    // Validate actions
    if (actions.some(a => !a.to || !a.subject || !a.body)) {
      toast.error('Por favor, preencha todos os campos do email');
      return;
    }

    if (trigger === 'emailReceived' && !labelFilter) {
      const confirm = window.confirm('Você não configurou nenhuma label. A automação será executada para TODOS os emails. Continuar?');
      if (!confirm) return;
    }

    if (trigger === 'scheduled' && !scheduleTime) {
      toast.error('Por favor, configure o horário de execução');
      return;
    }

    setIsSaving(true);

    try {
      // Convert new files to base64
      const newAttachmentsBase64 = await Promise.all(
        attachments.map(async (file) => {
          return new Promise<{ name: string; content: string; mimeType: string }>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const base64Content = (reader.result as string).split(',')[1];
              resolve({
                name: file.name,
                content: base64Content,
                mimeType: file.type
              });
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
        })
      );

      // Combine saved attachments with new ones
      const allAttachments = [...savedAttachments, ...newAttachmentsBase64];

      const automation = {
        name: name.trim(),
        trigger: {
          type: trigger,
          labelFilter: trigger === 'emailReceived' ? labelFilter : undefined,
          scheduleTime: trigger === 'scheduled' ? scheduleTime : undefined
        },
        actions: actions.map(a => ({
          ...a,
          attachments: allAttachments
        })),
        active: false // Save as draft
      };

      let response;
      if (isEditMode && id) {
        response = await gmailApi.updateAutomation(id, automation, userId);
      } else {
        response = await gmailApi.createAutomation(automation, userId);
      }

      if (response.status === 'success') {
        toast.success(
          isEditMode
            ? 'Automação atualizada com sucesso!'
            : 'Automação criada com sucesso! Active-a na lista de automações.'
        );
        navigate('/gmail/automations');
      } else {
        toast.error(response.message || 'Erro ao salvar automação');
      }
    } catch (error) {
      console.error('[GmailAutomationSimple] Erro ao salvar automação:', error);
      toast.error('Erro ao salvar automação');
    } finally {
      setIsSaving(false);
    }
  };

  // Mostrar loading enquanto verifica conexão ou carrega dados
  if (isLoadingTokenConfig || isLoading) {
    return (
      <Container>
        <Header>
          <BackButton onClick={() => navigate('/gmail/automations')}>
            <ArrowLeft size={20} />
            Voltar
          </BackButton>
          <Title>{isLoadingTokenConfig ? 'Verificando conexão...' : 'Carregando...'}</Title>
        </Header>
        <Content>
          <Section>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem' }}>
              <Loader2 size={32} className="spinning" />
            </div>
          </Section>
        </Content>
      </Container>
    );
  }

  // Se não tiver token, não renderizar (useEffect irá redirecionar)
  if (!hasToken) {
    return null;
  }

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/gmail/automations')}>
          <ArrowLeft size={20} />
          Voltar
        </BackButton>
        <Title>{isEditMode ? 'Editar Automação' : 'Nova Automação'}</Title>
      </Header>

      <Content>
        {/* Nome da Automação */}
        <Section>
          <SectionTitle>
            <Zap size={20} />
            Nome da Automação
          </SectionTitle>
          <FormGroup>
            <Input
              type="text"
              placeholder="Ex: Resposta automática para leads"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </FormGroup>
        </Section>
        {/* Trigger Selection */}
        <Section>
          <SectionTitle>
            <Play size={20} />
            Quando executar?
          </SectionTitle>

          <TriggerContainer>
            {triggerOptions.map(option => (
              <TriggerCard
                key={option.type}
                selected={trigger === option.type}
                gradient={option.gradient}
                onClick={() => setTrigger(option.type)}
              >
                <option.icon />
                <span>{option.label}</span>
                <p>{option.description}</p>
              </TriggerCard>
            ))}
          </TriggerContainer>

          {/* Conditional fields based on trigger */}
          <AnimatePresence mode="wait">
            {trigger === 'emailReceived' && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                <FormGroup>
                  <Label>
                    Filtrar por label (opcional)
                    <Info size={16} />
                  </Label>
                  <Input
                    type="text"
                    placeholder="Ex: IMPORTANTE"
                    value={labelFilter}
                    onChange={(e) => setLabelFilter(e.target.value)}
                  />
                  <InfoBox>
                    <Info size={18} />
                    <span>{explanationText}</span>
                  </InfoBox>
                </FormGroup>
              </motion.div>
            )}

            {trigger === 'scheduled' && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                <FormGroup>
                  <Label>
                    <Clock size={16} />
                    Horário de execução
                  </Label>
                  <Input
                    type="time"
                    value={scheduleTime}
                    onChange={(e) => setScheduleTime(e.target.value)}
                  />
                </FormGroup>
              </motion.div>
            )}
          </AnimatePresence>
        </Section>

        {/* Email Action */}
        <Section>
          <SectionTitle>
            <Send size={20} />
            O que fazer?
          </SectionTitle>

          {actions.map((action, index) => (
            <div key={index}>
              <FormGroup>
                <Label>Para (email destinatário)</Label>
                <Input
                  type="email"
                  placeholder="exemplo@email.com"
                  value={action.to}
                  onChange={(e) => {
                    const newActions = [...actions];
                    newActions[index].to = e.target.value;
                    setActions(newActions);
                  }}
                />
              </FormGroup>

              <FormGroup>
                <Label>Assunto</Label>
                <Input
                  type="text"
                  placeholder="Digite o assunto do email"
                  value={action.subject}
                  onChange={(e) => {
                    const newActions = [...actions];
                    newActions[index].subject = e.target.value;
                    setActions(newActions);
                  }}
                />
              </FormGroup>

              <FormGroup>
                <Label>Mensagem</Label>
                <Textarea
                  placeholder="Digite a mensagem do email"
                  value={action.body}
                  onChange={(e) => {
                    const newActions = [...actions];
                    newActions[index].body = e.target.value;
                    setActions(newActions);
                  }}
                />
              </FormGroup>

              {/* File Attachments */}
              <AttachmentSection>
                <Label>
                  <Paperclip size={16} />
                  Anexos (opcional)
                </Label>

                <FileInput
                  id="file-upload"
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                />
                <UploadButton htmlFor="file-upload">
                  <Paperclip size={18} />
                  Adicionar arquivos
                </UploadButton>

                {(savedAttachments.length > 0 || attachments.length > 0) && (
                  <FileList>
                    {savedAttachments.map((attachment, index) => (
                      <FileChip key={`saved-${index}`}>
                        {attachment.name}
                        <button onClick={() => removeSavedAttachment(attachment.name)}>
                          <X />
                        </button>
                      </FileChip>
                    ))}
                    {attachments.map((file, fileIndex) => (
                      <FileChip key={`new-${fileIndex}`}>
                        {file.name}
                        <button onClick={() => removeFile(file)}>
                          <X />
                        </button>
                      </FileChip>
                    ))}
                  </FileList>
                )}
              </AttachmentSection>
            </div>
          ))}
        </Section>

        {/* Dynamic Preview */}
        <Section>
          <SectionTitle>
            <CheckCircle size={20} />
            Prévia da automação
          </SectionTitle>
          <PreviewBox>
            {actions.some(a => a.to || a.subject || a.body) ? (
              <PreviewSection>
                {/* Trigger Info */}
                <PreviewItem>
                  {trigger === 'manual' && <Play size={20} />}
                  {trigger === 'emailReceived' && <Mail size={20} />}
                  {trigger === 'scheduled' && <Clock size={20} />}
                  <PreviewContent>
                    <PreviewTitle>
                      {trigger === 'manual' && 'Executar manualmente'}
                      {trigger === 'emailReceived' && 'Ao receber email'}
                      {trigger === 'scheduled' && 'Executar agendado'}
                    </PreviewTitle>
                    {trigger === 'emailReceived' && labelFilter && (
                      <PreviewDetail>Com label: {labelFilter}</PreviewDetail>
                    )}
                    {trigger === 'scheduled' && scheduleTime && (
                      <PreviewDetail>Horário: {scheduleTime}</PreviewDetail>
                    )}
                  </PreviewContent>
                </PreviewItem>

                {/* Actions */}
                {actions.map((action, index) => (
                  <PreviewItem key={index}>
                    <Send size={20} />
                    <PreviewContent>
                      <PreviewTitle>Enviar email</PreviewTitle>
                      {action.to && <PreviewDetail>Para: {action.to}</PreviewDetail>}
                      {action.subject && <PreviewDetail>Assunto: {action.subject}</PreviewDetail>}
                      {action.body && (
                        <PreviewDetail>
                          Mensagem: {action.body.length > 50 ? action.body.substring(0, 50) + '...' : action.body}
                        </PreviewDetail>
                      )}
                      {(attachments.length > 0 || savedAttachments.length > 0) && (
                        <PreviewDetail>
                          <Paperclip size={14} style={{ display: 'inline', marginRight: '4px' }} />
                          {attachments.length + savedAttachments.length} arquivo{(attachments.length + savedAttachments.length) > 1 ? 's' : ''}
                        </PreviewDetail>
                      )}
                    </PreviewContent>
                  </PreviewItem>
                ))}
              </PreviewSection>
            ) : (
              <PreviewEmpty>
                Configure os campos acima para visualizar a prévia da automação
              </PreviewEmpty>
            )}
          </PreviewBox>
        </Section>

        {/* Action Buttons */}
        <ButtonGroup>
          <Button variant="secondary" onClick={() => navigate('/gmail/automations')} disabled={isSaving}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 size={18} className="spinning" />
                Salvando...
              </>
            ) : (
              <>
                <Save size={18} />
                {isEditMode ? 'Atualizar' : 'Salvar como Rascunho'}
              </>
            )}
          </Button>
        </ButtonGroup>
      </Content>
    </Container>
  );
};

export default GmailAutomationSimple;
